import { memo, useMemo } from "react";

import { useProduct } from "@shopify/hydrogen-react";

import { formatProduct } from "@root/tablescape/utilities";

import { ProductBridgeView } from "./ProductBridge.view";

type Props = {
  labelCtaText?: string;
  linkAriaLabel?: string;
  onLabelCtaClick?: () => void;
};

const ProductBridgeBase = ({
  labelCtaText,
  linkAriaLabel,
  onLabelCtaClick,
}: Props) => {
  const { product } = useProduct();

  const formattedProduct = useMemo(() => formatProduct(product), [product]);

  const crossProductBridgeOptions = useMemo(
    () =>
      formattedProduct.crossProductBridge?.options.map((option) => ({
        label: option.label,
        to: option.product.onlineStoreUrl,
        type: "link" as const,
      })),
    [formattedProduct.crossProductBridge?.options]
  );

  const selectedOption = useMemo(
    () =>
      formattedProduct.crossProductBridge?.options.find(
        (option) => option.product.handle === product.handle
      )?.label,
    [formattedProduct.crossProductBridge?.options, product.handle]
  );

  const selectedIndex = useMemo(
    () =>
      crossProductBridgeOptions?.findIndex(
        (option) => option.label === selectedOption
      ) ?? 0,
    [crossProductBridgeOptions, selectedOption]
  );

  if (!formattedProduct.crossProductBridge) {
    return null;
  }

  return (
    <ProductBridgeView
      label={formattedProduct.crossProductBridge.label}
      linkAriaLabel={linkAriaLabel}
      options={crossProductBridgeOptions}
      selectedIndex={selectedIndex}
      selectedOption={selectedOption}
      {...(labelCtaText &&
        onLabelCtaClick && {
          labelCtaText,
          onLabelCtaClick,
        })}
    />
  );
};

export const ProductBridge = memo(ProductBridgeBase);
